"use client"; // Make this a client component

import {
  Building,
  ClockIcon,
  PhoneIcon,
  BoxIcon,
  Clock,
  Mail,
} from "lucide-react";
import { useTranslations } from "next-intl";
import { useState } from "react";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/trpc/react"; // Import the tRPC client

import { GradientOutlineButton } from "../design/gradient-outline-button";
import Header from "../design/header";
import CopyButton from "../footer/copy-button";
import { Separator } from "../ui/separator";
import Turnstile, { TURNSTILE_FIELD, TURNSTILE_SITE_KEY } from "./turnstile";

export default function ContactSection() {
  // State for form inputs
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  // Bumped after every submit to remount Turnstile, its tokens are single-use
  const [turnstileKey, setTurnstileKey] = useState(0);
  const [turnstilePending, setTurnstilePending] = useState(false);

  // tRPC mutation hook
  const sendEmailMutation = api.email.send.useMutation({
    onSuccess: () => {
      // Clear form on success
      setName("");
      setEmail("");
      setMessage("");
    },
    onSettled: () => setTurnstileKey((k) => k + 1),
  });

  const turnstileRejected = sendEmailMutation.error?.data?.code === "FORBIDDEN";

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const token = new FormData(e.currentTarget).get(TURNSTILE_FIELD);
    const turnstileToken = typeof token === "string" ? token : "";

    if (TURNSTILE_SITE_KEY && !turnstileToken) {
      setTurnstilePending(true);
      return;
    }

    setTurnstilePending(false);
    sendEmailMutation.mutate({ name, email, message, turnstileToken });
  };

  const t = useTranslations("staticComponents.contactSection");

  return (
    <section className="mx-auto flex w-full max-w-2xl flex-col gap-6 px-5 lg:max-w-7xl xl:px-20">
      {/* ...your existing static JSX for contact info cards... */}
      <div className="flex flex-col gap-4 py-6 sm:hidden">
        <div className="min-w-full rounded-[20px] bg-gradient-to-r from-gray-950/50 via-gray-950/[3%] to-gray-950/50 p-6 backdrop-blur-[22px]">
          <div className="flex flex-col gap-4">
            <div className="flex gap-2">
              <Building className="h-6 w-6 shrink-0" />
              <p className="text-p1 font-semibold">
                {t("AdresDojazdowy.title")}
              </p>
            </div>
            <Separator className="bg-white/30 px-1" />
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <p className="text-p1">{t("AdresDojazdowy.cityTitle")}</p>
                <p className="text-p1">{t("AdresDojazdowy.city")}</p>
              </div>
              <div className="flex items-center justify-between">
                <p className="text-p1">{t("AdresDojazdowy.streetTitle")}</p>
                <p className="text-p1">{t("AdresDojazdowy.streetTitle")}</p>
              </div>
            </div>
          </div>
        </div>
        <div className="min-w-full rounded-[20px] bg-gradient-to-r from-gray-950/50 via-gray-950/[3%] to-gray-950/50 p-6 backdrop-blur-[22px]">
          <div className="flex flex-col gap-4">
            <div className="flex gap-2">
              <ClockIcon className="h-6 w-6 shrink-0" />
              <p className="text-p1 font-semibold">{t("workingHours.title")}</p>
            </div>
            <Separator className="bg-white/30 px-1" />
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <p className="text-p1">{t("workingHours.weekdayTitle")}</p>
                <p className="text-p1">{t("workingHours.weekday")}</p>
              </div>
              <div className="flex items-center justify-between">
                <p className="text-p1">{t("workingHours.weekendTitle")}</p>
                <p className="text-p1">{t("workingHours.weekend")}</p>
              </div>
            </div>
          </div>
        </div>
        <div className="min-w-full rounded-[20px] bg-gradient-to-r from-gray-950/50 via-gray-950/[3%] to-gray-950/50 p-6 backdrop-blur-[22px]">
          <div className="flex flex-col gap-4">
            <div className="flex gap-2">
              <PhoneIcon className="h-6 w-6 shrink-0" />
              <p className="text-p1 font-semibold">{t("contact.title")}</p>
            </div>
            <Separator className="bg-white/30 px-1" />
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <p className="text-p1">{t("contact.phoneTitle")}</p>
                <p className="text-p1">{t("contact.phone")}</p>
              </div>
              <div className="flex items-center justify-between">
                <p className="text-p1">{t("contact.emailTitle")}</p>
                <p className="text-p1">{t("contact.email")}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <form
        onSubmit={handleSubmit}
        className="mx-auto flex w-full max-w-7xl flex-col justify-center gap-8 lg:flex-row xl:items-stretch xl:justify-between"
      >
        <div className="flex w-full flex-col gap-6 lg:w-1/2 lg:max-w-md">
          <Header className="mt-16 pl-0 lg:mt-0 lg:hidden">
            {t("Forms.title")}
          </Header>
          <div className="grid w-full items-center gap-2">
            <Label htmlFor="name" className="text-p1 font-semibold">
              {t("Forms.name")}
            </Label>
            <div className="relative flex h-12 w-full items-center rounded-2xl border border-zinc-500 bg-gradient-to-r from-gray-950/50 via-gray-950/[3%] to-gray-950/50 px-3 py-2 text-p1 backdrop-blur-[22px]">
              <Input
                type="text"
                id="name"
                placeholder={t("Forms.namePlaceholder")}
                className="h-auto w-full border-none bg-transparent p-0 text-p2 focus-visible:ring-0 focus-visible:ring-offset-0"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>
          </div>

          <div className="grid w-full items-center gap-2">
            <Label htmlFor="email" className="text-p1 font-semibold">
              {t("Forms.email")}
            </Label>
            <div className="relative flex h-12 w-full items-center rounded-2xl border border-zinc-500 bg-gradient-to-r from-gray-950/50 via-gray-950/[3%] to-gray-950/50 px-3 py-2 text-p1 backdrop-blur-[22px]">
              <Input
                type="email"
                id="email"
                placeholder={t("Forms.emailPlaceholder")}
                className="h-auto w-full border-none bg-transparent p-0 text-p2 focus-visible:ring-0 focus-visible:ring-offset-0"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
          </div>

          <div className="grid w-full items-center gap-2">
            <Label htmlFor="message" className="text-p1 font-semibold">
              {t("Forms.msg")}
            </Label>
            <div className="relative flex h-full min-h-[140px] w-full flex-grow rounded-2xl border border-zinc-500 bg-gradient-to-r from-gray-950/50 via-gray-950/[3%] to-gray-950/50 px-3 py-2 text-p1 backdrop-blur-[22px]">
              <Textarea
                id="message"
                placeholder={t("Forms.msgPlaceholder")}
                className="h-full w-full resize-none border-none bg-transparent p-0 text-p2 focus-visible:ring-0 focus-visible:ring-offset-0"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                required
              />
            </div>
          </div>

          <Turnstile key={turnstileKey} action="contact" />

          {/* Submit Button and Status Messages */}
          <div className="relative mt-4">
            <GradientOutlineButton
              type="submit"
              disabled={sendEmailMutation.isPending}
            >
              {sendEmailMutation.isPending ? "Wysyłanie..." : t("Forms.submit")}
            </GradientOutlineButton>

            <div className="absolute -top-8 left-0 flex flex-col items-start">
              {sendEmailMutation.isSuccess && (
                <p className="text-green-500">{t("Forms.success")}</p>
              )}
              {sendEmailMutation.isError && !turnstileRejected && (
                <p className="text-red-500">
                  {t("Forms.error")}:{" "}
                  {sendEmailMutation.error?.message ??
                    "Nie udało się wysłać wiadomości."}
                </p>
              )}
              {sendEmailMutation.error?.data?.zodError?.fieldErrors.name && (
                <span className="text-sm text-red-500">
                  {sendEmailMutation.error.data.zodError.fieldErrors.name}
                </span>
              )}
              {sendEmailMutation.error?.data?.zodError?.fieldErrors.email && (
                <span className="text-sm text-red-500">
                  {sendEmailMutation.error.data.zodError.fieldErrors.email}
                </span>
              )}
              {sendEmailMutation.error?.data?.zodError?.fieldErrors.message && (
                <span className="text-sm text-red-500">
                  {sendEmailMutation.error.data.zodError.fieldErrors.message}
                </span>
              )}
            </div>
          </div>

          {/* In normal flow, these are too long for the overlay above */}
          {(turnstilePending || turnstileRejected) && (
            <p className="text-sm text-red-500">
              {turnstilePending
                ? t("Forms.turnstilePending")
                : t("Forms.turnstileFailed")}
            </p>
          )}
        </div>

        {/* ...your existing static JSX for the right column... */}
        <div className="mt-4 hidden min-h-[340px] w-full justify-around rounded-[20px] bg-gradient-to-r from-gray-950/50 via-gray-950/[3%] to-gray-950/50 px-4 py-1 backdrop-blur-[22px] sm:flex lg:w-1/2 lg:justify-between lg:px-2 xl:px-4">
          <div className="flex min-h-full flex-col justify-around">
            <div className="flex min-h-20 items-center gap-4">
              <Clock className="h-6 w-6 shrink-0" />
              <p className="text-nowrap text-p1 font-semibold">
                {t("workingHours.title")}
              </p>
            </div>
            <div className="flex min-h-20 items-center gap-4">
              <PhoneIcon className="h-6 w-6 shrink-0" />
              <p className="text-nowrap text-p1 font-semibold">
                {t("contact.title")}
              </p>
            </div>
            <div className="flex min-h-20 items-center gap-4">
              <BoxIcon className="h-6 w-6 shrink-0" />
              <p className="text-nowrap text-p1 font-semibold">
                {t("AdresDojazdowy.title")}
              </p>
            </div>
          </div>
          <div className="flex min-h-full flex-none flex-col justify-around gap-2 px-2 lg:gap-0">
            <div className="min-h-20 border-l border-white/30"></div>
            <div className="min-h-20 border-l border-white/30"></div>
            <div className="min-h-20 border-l border-white/30"></div>
          </div>

          <div className="flex min-h-full justify-between">
            <div className="flex h-full flex-col justify-around pr-4">
              <div className="flex min-h-20 flex-col justify-center">
                <p className="text-nowrap text-p1">
                  {t("workingHours.weekdayTitle")}
                </p>
                <p className="text-nowrap text-p1">
                  {t("workingHours.weekendTitle")}
                </p>
              </div>

              <div className="flex min-h-20 flex-col justify-center">
                <p className="text-nowrap text-p1">{t("contact.phoneTitle")}</p>
                <p className="text-nowrap text-p1">{t("contact.emailTitle")}</p>
              </div>

              <div className="flex min-h-20 flex-col justify-center">
                <p className="text-nowrap text-p1">
                  {t("AdresDojazdowy.cityTitle")}
                </p>
                <p className="text-nowrap text-p1">
                  {t("AdresDojazdowy.streetTitle")}
                </p>
              </div>
            </div>
            <div className="flex h-full flex-col justify-around">
              <div className="flex min-h-20 flex-col justify-center">
                <p className="text-nowrap text-p1">
                  {t("workingHours.weekday")}
                </p>
                <p className="text-nowrap text-p1">
                  {t("workingHours.weekend")}
                </p>
              </div>

              <div className="flex min-h-20 flex-col justify-center">
                <div className="flex gap-2">
                  <p className="text-nowrap text-p1">{t("contact.phone")}</p>
                  <CopyButton text="+48 660 62 00 99" />
                </div>
                <div className="flex gap-2">
                  <p className="text-nowrap text-p1">{t("contact.email")}</p>
                  <CopyButton text="arena@lasercamp.pl" />
                </div>
              </div>

              <div className="flex min-h-20 flex-col justify-center">
                <p className="text-nowrap text-p1">
                  {t("AdresDojazdowy.city")}
                </p>
                <p className="text-nowrap text-p1">
                  {t("AdresDojazdowy.street")}
                </p>
              </div>
            </div>
          </div>
        </div>
      </form>
    </section>
  );
}
